import { Color } from '@tiptap/extension-color'
import { Highlight } from '@tiptap/extension-highlight'
import Image from '@tiptap/extension-image'
import Link from '@tiptap/extension-link'
import Placeholder from '@tiptap/extension-placeholder'
import { Table } from '@tiptap/extension-table'
import { TableCell } from '@tiptap/extension-table-cell'
import { TableHeader } from '@tiptap/extension-table-header'
import { TableRow } from '@tiptap/extension-table-row'
import { TextAlign } from '@tiptap/extension-text-align'
import { TextStyle } from '@tiptap/extension-text-style'
import Underline from '@tiptap/extension-underline'
import { EditorContent, useEditor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import DOMPurify from 'dompurify'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { api, resolveApiAssetUrl } from '../lib/api'
import { useToast } from '../ui/ToastProvider'

const TEXT_COLORS = [
  { label: '기본', value: '' },
  { label: '검정', value: '#0f172a' },
  { label: '회색', value: '#475569' },
  { label: '빨강', value: '#dc2626' },
  { label: '주황', value: '#ea580c' },
  { label: '초록', value: '#16a34a' },
  { label: '파랑', value: '#0284c7' },
]

const HIGHLIGHT_COLORS = [
  { label: '형광 없음', value: '' },
  { label: '노랑', value: '#fef08a' },
  { label: '초록', value: '#bbf7d0' },
  { label: '파랑', value: '#bae6fd' },
  { label: '빨강', value: '#fecaca' },
]

function sanitizeNoticeHtml(html) {
  return DOMPurify.sanitize(html || '', {
    USE_PROFILES: { html: true },
    ADD_ATTR: ['style', 'class', 'target', 'rel', 'colspan', 'rowspan'],
  })
}

function ToolbarBtn({ onClick, active, disabled, title, children }) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      disabled={disabled}
      className={`rounded border px-2 py-1 text-xs font-medium ${
        active
          ? 'border-sky-300 bg-sky-100 text-sky-900'
          : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
      } ${disabled ? 'cursor-not-allowed opacity-40' : ''}`}
    >
      {children}
    </button>
  )
}

export default function SystemNoticeRichEditor({ value, onChange }) {
  const { show } = useToast()
  const [sourceOpen, setSourceOpen] = useState(false)
  const [sourceHtml, setSourceHtml] = useState(value || '')

  const editor = useEditor({
    immediatelyRender: false,
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
      TextStyle,
      Color,
      Highlight.configure({ multicolor: true }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        autolink: true,
        defaultProtocol: 'https',
      }),
      Image.configure({
        HTMLAttributes: { class: 'max-w-full h-auto rounded-md' },
      }),
      Table.configure({
        resizable: true,
      }),
      TableRow,
      TableHeader,
      TableCell,
      Placeholder.configure({
        placeholder: '공지 내용을 입력하세요',
      }),
    ],
    content: value || '',
    editorProps: {
      attributes: {
        class: 'focus:outline-none',
      },
    },
    onUpdate: ({ editor: ed }) => {
      const next = ed.getHTML()
      onChange(next)
      if (sourceOpen) {
        setSourceHtml(next)
      }
    },
  })

  const sanitizedPreview = useMemo(() => sanitizeNoticeHtml(sourceHtml), [sourceHtml])

  useEffect(() => {
    if (!editor) return
    const incoming = value || ''
    const cur = editor.getHTML()
    if (incoming !== cur) {
      editor.commands.setContent(incoming, false)
    }
  }, [value, editor])

  useEffect(() => {
    if (sourceOpen) {
      setSourceHtml(editor?.getHTML() || value || '')
    }
  }, [sourceOpen, editor, value])

  const addImage = useCallback(() => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'image/png,image/jpeg,image/jpg,image/webp,image/gif'
    input.onchange = async () => {
      const file = input.files?.[0]
      if (!file || !editor) return
      const fd = new FormData()
      fd.append('file', file)
      try {
        const res = await api.post('/files/upload/system-notice-image', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        const body = res?.data?.data ?? res?.data
        const fileId = body?.fileId
        if (!fileId) {
          show('이미지 업로드에 실패했습니다', 'error')
          return
        }
        const url = resolveApiAssetUrl(fileId)
        editor.chain().focus().setImage({ src: url }).run()
      } catch (err) {
        const msg = err?.response?.data?.message || '이미지 업로드에 실패했습니다'
        show(msg, 'error')
      }
    }
    input.click()
  }, [editor, show])

  const setLink = useCallback(() => {
    if (!editor) return
    const prev = editor.getAttributes('link').href || ''
    const url = window.prompt('링크 URL', prev || 'https://')
    if (url === null) return
    if (url.trim() === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run()
      return
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url.trim() }).run()
  }, [editor])

  const applySourceHtml = useCallback(() => {
    if (!editor) return
    const safeHtml = sanitizeNoticeHtml(sourceHtml)
    editor.commands.setContent(safeHtml, false)
    onChange(safeHtml)
    setSourceHtml(safeHtml)
    show('HTML을 안전하게 정리해 적용했습니다', 'success')
  }, [editor, onChange, show, sourceHtml])

  const applyTextColor = useCallback((color) => {
    if (!editor) return
    if (!color) {
      editor.chain().focus().unsetColor().run()
      return
    }
    editor.chain().focus().setColor(color).run()
  }, [editor])

  const applyHighlight = useCallback((color) => {
    if (!editor) return
    if (!color) {
      editor.chain().focus().unsetHighlight().run()
      return
    }
    editor.chain().focus().setHighlight({ color }).run()
  }, [editor])

  if (!editor) {
    return (
      <div
        data-testid="system-notice-body-editor"
        className="system-notice-tiptap min-h-[280px] rounded-lg border border-slate-200 bg-slate-50"
        aria-hidden
      />
    )
  }

  return (
    <div data-testid="system-notice-body-editor" className="system-notice-tiptap">
      <div className="tiptap-toolbar flex flex-wrap items-center gap-1 border-b border-slate-200 bg-slate-50 px-2 py-2">
        <ToolbarBtn
          title="제목 1"
          active={editor.isActive('heading', { level: 1 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        >
          H1
        </ToolbarBtn>
        <ToolbarBtn
          title="제목 2"
          active={editor.isActive('heading', { level: 2 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        >
          H2
        </ToolbarBtn>
        <ToolbarBtn
          title="제목 3"
          active={editor.isActive('heading', { level: 3 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        >
          H3
        </ToolbarBtn>
        <span className="mx-1 text-slate-300">|</span>
        <ToolbarBtn
          title="굵게"
          active={editor.isActive('bold')}
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          <strong>B</strong>
        </ToolbarBtn>
        <ToolbarBtn
          title="기울임"
          active={editor.isActive('italic')}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          <em>I</em>
        </ToolbarBtn>
        <ToolbarBtn
          title="밑줄"
          active={editor.isActive('underline')}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
        >
          <span className="underline">U</span>
        </ToolbarBtn>
        <ToolbarBtn
          title="취소선"
          active={editor.isActive('strike')}
          onClick={() => editor.chain().focus().toggleStrike().run()}
        >
          <s>S</s>
        </ToolbarBtn>
        <span className="mx-1 text-slate-300">|</span>
        <ToolbarBtn
          title="문단"
          active={editor.isActive('paragraph')}
          onClick={() => editor.chain().focus().setParagraph().run()}
        >
          문단
        </ToolbarBtn>
        <ToolbarBtn
          title="인용문"
          active={editor.isActive('blockquote')}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
        >
          인용
        </ToolbarBtn>
        <ToolbarBtn
          title="코드블록"
          active={editor.isActive('codeBlock')}
          onClick={() => editor.chain().focus().toggleCodeBlock().run()}
        >
          코드
        </ToolbarBtn>
        <ToolbarBtn
          title="구분선"
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
        >
          구분선
        </ToolbarBtn>
        <span className="mx-1 text-slate-300">|</span>
        <ToolbarBtn
          title="왼쪽 정렬"
          active={editor.isActive({ textAlign: 'left' })}
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
        >
          좌
        </ToolbarBtn>
        <ToolbarBtn
          title="가운데 정렬"
          active={editor.isActive({ textAlign: 'center' })}
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
        >
          중
        </ToolbarBtn>
        <ToolbarBtn
          title="오른쪽 정렬"
          active={editor.isActive({ textAlign: 'right' })}
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
        >
          우
        </ToolbarBtn>
        <select
          title="글자색"
          value={editor.getAttributes('textStyle').color || ''}
          onChange={(e) => applyTextColor(e.target.value)}
          className="rounded border border-slate-200 bg-white px-2 py-1 text-xs text-slate-700"
        >
          {TEXT_COLORS.map((color) => (
            <option key={color.label} value={color.value}>
              글자 {color.label}
            </option>
          ))}
        </select>
        <select
          title="배경색"
          value={editor.getAttributes('highlight').color || ''}
          onChange={(e) => applyHighlight(e.target.value)}
          className="rounded border border-slate-200 bg-white px-2 py-1 text-xs text-slate-700"
        >
          {HIGHLIGHT_COLORS.map((color) => (
            <option key={color.label} value={color.value}>
              배경 {color.label}
            </option>
          ))}
        </select>
        <span className="mx-1 text-slate-300">|</span>
        <ToolbarBtn
          title="글머리 목록"
          active={editor.isActive('bulletList')}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        >
          • 목록
        </ToolbarBtn>
        <ToolbarBtn
          title="번호 목록"
          active={editor.isActive('orderedList')}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        >
          1. 목록
        </ToolbarBtn>
        <span className="mx-1 text-slate-300">|</span>
        <ToolbarBtn title="링크" active={editor.isActive('link')} onClick={setLink}>
          링크
        </ToolbarBtn>
        <ToolbarBtn title="이미지 삽입" onClick={addImage}>
          이미지
        </ToolbarBtn>
        <span className="mx-1 text-slate-300">|</span>
        <ToolbarBtn
          title="표 삽입"
          onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
        >
          표
        </ToolbarBtn>
        <ToolbarBtn
          title="행 추가"
          disabled={!editor.can().addRowAfter()}
          onClick={() => editor.chain().focus().addRowAfter().run()}
        >
          행+
        </ToolbarBtn>
        <ToolbarBtn
          title="열 추가"
          disabled={!editor.can().addColumnAfter()}
          onClick={() => editor.chain().focus().addColumnAfter().run()}
        >
          열+
        </ToolbarBtn>
        <ToolbarBtn
          title="표 삭제"
          disabled={!editor.can().deleteTable()}
          onClick={() => editor.chain().focus().deleteTable().run()}
        >
          표삭제
        </ToolbarBtn>
        <span className="mx-1 text-slate-300">|</span>
        <ToolbarBtn
          title="서식 제거"
          onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}
        >
          서식 제거
        </ToolbarBtn>
        <ToolbarBtn
          title="HTML 소스"
          active={sourceOpen}
          onClick={() => setSourceOpen((open) => !open)}
        >
          HTML
        </ToolbarBtn>
        <span className="mx-1 text-slate-300">|</span>
        <ToolbarBtn
          title="실행 취소"
          disabled={!editor.can().undo()}
          onClick={() => editor.chain().focus().undo().run()}
        >
          ↶
        </ToolbarBtn>
        <ToolbarBtn
          title="다시 실행"
          disabled={!editor.can().redo()}
          onClick={() => editor.chain().focus().redo().run()}
        >
          ↷
        </ToolbarBtn>
      </div>
      <EditorContent editor={editor} />
      {sourceOpen && (
        <div className="border-t border-slate-200 bg-slate-50 p-3">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <div>
              <div className="text-sm font-semibold text-slate-800">HTML 소스</div>
              <p className="text-xs text-slate-500">
                적용 시 위험한 스크립트와 속성은 자동으로 제거됩니다.
              </p>
            </div>
            <button
              type="button"
              onClick={applySourceHtml}
              className="rounded bg-brand-sky px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-sky/90"
            >
              HTML 적용
            </button>
          </div>
          <textarea
            value={sourceHtml}
            onChange={(e) => setSourceHtml(e.target.value)}
            className="min-h-[180px] w-full rounded border border-slate-300 bg-white p-3 font-mono text-xs text-slate-800 focus:border-brand-sky focus:outline-none focus:ring-2 focus:ring-brand-sky/20"
            spellCheck={false}
          />
          <div className="mt-3 rounded border border-slate-200 bg-white p-3">
            <div className="mb-2 text-xs font-semibold text-slate-500">안전 미리보기</div>
            <div
              className="system-notice-html"
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: sanitizedPreview }}
            />
          </div>
        </div>
      )}
    </div>
  )
}
